import { storageService } from './storage';
import { queryClient } from './queryClient';
import { notificationService } from './notifications';
import { isJwtSessionEnded, getMsUntilJwtSessionEnd } from './jwtSession';
import { resetSessionInvalidationGate } from './sessionInvalidation';
import { removeOfflineSessionVault } from '@/lib/offlineSessionVault';
import { clearShiftActionOutbox } from '@/features/shifts/offline/shiftActionOutbox';
import { clearPatrolScanOutbox } from '@/features/patrol/offline/patrolScanOutbox';
import { logger } from '@/shared/logger';

export interface AuthSession {
  token: string;
  user: any;
}

class AuthService {
  private isInitialized = false;

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Initialize storage service
      await storageService.initialize();
      logger.info("authService.initialize.storageReady");
      
      // Try to restore session from storage
      const restored = await this.restoreSession();
      logger.info("authService.initialize.restoreAttempt", { restored });
      
      this.isInitialized = true;
    } catch (error) {
      logger.error('authService.initialize.failed', { error });
    }
  }

  /** True when stored token is past client session end (before JWT exp). */
  async isStoredSessionPastJwtEnd(): Promise<boolean> {
    const token = await storageService.getAuthToken();
    return !!token && isJwtSessionEnded(token);
  }

  /**
   * Schedules a one-shot logout at JWT exp minus buffer. Returns cleanup to clear the timer.
   */
  scheduleLogoutAtJwtSessionEnd(onFire: () => void | Promise<void>): () => void {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const arm = async () => {
      const token = await storageService.getAuthToken();
      if (!token) return;

      const ms = getMsUntilJwtSessionEnd(token);
      if (ms == null) return;

      if (ms <= 0) {
        await Promise.resolve(onFire());
        return;
      }

      timeoutId = setTimeout(() => {
        void Promise.resolve(onFire());
      }, ms);
    };

    void arm();

    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
  }

  async restoreSession(): Promise<boolean> {
    try {
      const token = await storageService.getAuthToken();
      const userData = await storageService.getUserData();
      logger.info("authService.restoreSession.read", {
        hasToken: !!token,
        hasUserData: !!userData,
      });

      if (token && userData) {
        // Check if token is expired
        if (isJwtSessionEnded(token)) {
          logger.warn("authService.restoreSession.tokenEnded");
          await this.clearSession();
          return false;
        }

        
        // Restore user data to query cache
        queryClient.setQueryData(['auth', 'user'], userData);
        
        return true;
      } else {
        return false;
      }
    } catch (error) {
      logger.error('authService.restoreSession.failed', { error });
      return false;
    }
  }

  async getRestoredUser(): Promise<any | null> {
    try {
      const token = await storageService.getAuthToken();
      const userData = await storageService.getUserData();

      if (token && userData && !isJwtSessionEnded(token)) {
        return userData;
      }
      return null;
    } catch (error) {
      logger.error('authService.getRestoredUser.failed', { error });
      return null;
    }
  }

  async saveSession(token: string, userData: any): Promise<void> {
    try {
      resetSessionInvalidationGate();
      logger.info("authService.saveSession.start", {
        hasToken: !!token,
        userId: userData?.id,
      });
      await storageService.setAuthToken(token);
      await storageService.setUserData(userData);
      
      // Update query cache
      queryClient.setQueryData(['auth', 'user'], userData);
      
      // Force re-initialization of notifications for the new session
      await notificationService.forceReinitialize();
      
    } catch (error) {
      logger.error('authService.saveSession.failed', { error });
      throw error;
    }
  }

  async clearSession(): Promise<void> {
    try {
      logger.info("authService.clearSession.start");
      const userData = await storageService.getUserData();
      const phone =
        (userData?.whatsapp_number as string | undefined) ??
        (await storageService.getPhoneNumber());
      if (phone) {
        await removeOfflineSessionVault(phone);
      }

      await clearShiftActionOutbox();
      await clearPatrolScanOutbox();

      await storageService.clearAuthData();
      logger.info("authService.clearSession.storageCleared");
      
      // Only clear auth-related data, not the entire cache
      queryClient.setQueryData(['auth', 'user'], null);
      queryClient.removeQueries({ queryKey: ['auth'] }); // More surgical
      
      // Clear notification state when user logs out
      notificationService.clearNotifications();
      
    } catch (error) {
      logger.error('authService.clearSession.failed', { error });
      throw error;
    }
  }

  async getStoredToken(): Promise<string | null> {
    const token = await storageService.getAuthToken();
    
    if (token && isJwtSessionEnded(token)) {
      await this.clearSession();
      return null;
    }
    
    return token;
  }

  async getStoredUser(): Promise<any | null> {
    return await storageService.getUserData();
  }

  isSessionValid(): boolean {
    const user = queryClient.getQueryData(['auth', 'user']);
    return !!user;
  }
}

export const authService = new AuthService();
